import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mail, RefreshCw } from "lucide-react";
import { verifyEmailApi, resendVerificationEmailApi, AuthApiError } from "@/services/auth.service";

type Status = "loading" | "success" | "error";

export function VerifyEmailPage() {
  const { id, hash } = useParams<{ id: string; hash: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");

  // Resend state
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  // ── Auto-verify on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!id || !hash) {
      setStatus("error");
      setMessage("Lien de vérification invalide.");
      return;
    }

    verifyEmailApi(id, hash, searchParams)
      .then((data) => {
        setStatus("success");
        setMessage(data.message);
        setVerifiedEmail(data.user.email);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err instanceof AuthApiError
            ? err.message
            : "Une erreur est survenue lors de la vérification.",
        );
      });
  }, [id, hash, searchParams]);

  // ── Resend verification email ─────────────────────────────────────────────
  const handleResend = async () => {
    if (!resendEmail) return;
    setIsResending(true);
    setResendMessage(null);
    setResendError(null);

    try {
      const data = await resendVerificationEmailApi(resendEmail);
      setResendMessage(data.message || "Email de vérification renvoyé avec succès.");
    } catch (err) {
      setResendError(
        err instanceof AuthApiError
          ? err.message
          : "Impossible de renvoyer l'email. Veuillez réessayer.",
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 font-sans">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100">
        {/* ── Loading ─────────────────────────────────────────────────────── */}
        {status === "loading" && (
          <div className="text-center space-y-4">
            <Loader2 size={48} className="text-indigo-500 animate-spin mx-auto" />
            <h1 className="text-2xl font-black text-[#003366]">Vérification en cours…</h1>
            <p className="text-gray-400 text-sm">Veuillez patienter.</p>
          </div>
        )}

        {/* ── Success ─────────────────────────────────────────────────────── */}
        {status === "success" && (
          <div className="text-center space-y-5">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-black text-[#003366]">Email vérifié !</h1>
            <p className="text-gray-500 text-sm">{message}</p>
            {verifiedEmail && <p className="text-sm font-bold text-gray-700">{verifiedEmail}</p>}
            <button
              type="button"
              onClick={() => navigate("/login", { state: { verifiedEmail } })}
              className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
            >
              Se connecter
            </button>
          </div>
        )}

        {/* ── Error ───────────────────────────────────────────────────────── */}
        {status === "error" && (
          <div className="space-y-6">
            <div className="text-center space-y-3">
              <XCircle size={48} className="text-red-500 mx-auto" />
              <h1 className="text-2xl font-black text-[#003366]">Vérification échouée</h1>
              <p className="text-gray-500 text-sm">{message}</p>
            </div>

            {/* Resend form */}
            <div className="border-t border-gray-100 pt-5 space-y-4">
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                Renvoyer l'email de vérification
              </p>
              <div className="relative group">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                  size={18}
                />
                <input
                  type="email"
                  placeholder="votre@email.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={isResending}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 focus:bg-white transition-all text-gray-900 font-semibold disabled:opacity-50"
                />
              </div>

              {resendMessage && (
                <p className="text-sm font-bold text-emerald-600">{resendMessage}</p>
              )}
              {resendError && <p className="text-sm font-bold text-red-600">{resendError}</p>}

              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || !resendEmail}
                className="w-full bg-indigo-600 text-white rounded-xl py-3.5 font-black text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} />
                    Renvoyer le mail
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
            >
              Retour à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
